"use client";
import Link from "next/link";
import Image from "next/image";
import { Button } from "../ui/button";
import { Menu, SparklesIcon, X } from "lucide-react";
import { useState, useEffect } from "react";

export function MainNav({ NavProps, className }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  const icons = {
    sparkles: <SparklesIcon className="size-3 self-start" />,
  };
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    const sectionIds = NavProps.menu
      .map((item) => item.href.replace("#", ""))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveSection(visible[0].target.id);
        }
      },
      { rootMargin: "-20% 0px -60% 0px", threshold: 0 }
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [NavProps.menu]);

  return (
    <nav
      className={`${className} h-16 bg-slate-50 shadow-md px-4 md:px-8 lg:px-24 flex justify-between items-center`}
    >
      {/* Logo */}
      <div className="relative w-44 md:w-48 h-10 my-3 cursor-pointer flex items-center">
        <Link href={NavProps.href}>
          <Image
            src={NavProps.image}
            fill
            alt={NavProps.alt}
            className="object-contain"
            priority
            sizes="(max-width: 768px) 176px, 192px"
          />
        </Link>
      </div>
      {/* Desktop Menu */}
      <ul className="hidden md:flex justify-between items-center space-x-1">
        {NavProps.menu.map((item, index) => {
          const sectionId = item.href.replace("#", "");
          const isActive = activeSection === sectionId;
          return (
            <li key={index}>
              <a
                href={item.href}
                className={`relative px-4 py-2 flex justify-center items-center rounded-md text-sm font-medium transition-all duration-300 ${
                  isActive
                    ? "text-primary"
                    : "text-gray-600 hover:text-primary hover:bg-primary/5"
                }`}
              >
                {item.name}
                {icons[item.icon]}
                <span
                  className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-primary rounded-full transition-all duration-300 ${
                    isActive ? "w-3/4" : "w-0"
                  }`}
                />
              </a>
            </li>
          );
        })}
      </ul>
      {/* Mobile Menu Button */}
      <div className="md:hidden">
        <Button
          onClick={toggleMobileMenu}
          size="icon"
          variant="ghost"
          className="bg-inherit hover:bg-inherit"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </Button>
      </div>{" "}
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 top-16 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={toggleMobileMenu}
        >
          <div
            className="bg-white w-full shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <ul className="flex flex-col py-4">
              {NavProps.menu.map((item, index) => {
                const sectionId = item.href.replace("#", "");
                const isActive = activeSection === sectionId;
                return (
                  <li key={index} className="py-1">
                    <a
                      href={item.href}
                      className={`px-6 py-2 text-lg transition-all duration-200 block w-full border-l-4 ${
                        isActive
                          ? "border-primary text-primary bg-primary/5 font-semibold"
                          : "border-transparent text-gray-600 hover:bg-primary/5 hover:text-primary hover:border-primary/30"
                      }`}
                      onClick={toggleMobileMenu}
                    >
                      {item.name}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
    </nav>
  );
}
